import { Link } from "react-router-dom";

import { LangToggle } from "@/components/lang-toggle";
import { ThemeToggle } from "@/components/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { useHighlightedPathname } from "@/lib/highlighted-pathname";
import { useT } from "@/lib/i18n";
import type { TranslationKey } from "@/lib/i18n-dict";

const CRUMB_KEYS: Record<string, TranslationKey> = {
  chat: "nav.chat",
  agents: "nav.agents",
  knowledge: "nav.knowledge",
  channels: "nav.channels",
  llm: "nav.llm",
  mcp: "nav.mcp",
  evaluations: "nav.activity",
  skills: "nav.skills",
  teams: "nav.teams",
  settings: "nav.settings",
};

/**
 * Sticky workspace header. Shows the brand wordmark, a tiny version
 * chip, and a breadcrumb derived from the active route. Theme + i18n +
 * user controls live on the right. Translucent so content scrolling
 * underneath stays slightly visible.
 *
 * The breadcrumb segment is keyed off the dictionary so it updates
 * automatically when the user toggles language.
 */
export function AppHeader({ children }: { children?: React.ReactNode }) {
  const pathname = useHighlightedPathname();
  const t = useT();
  const seg = pathname.split("/").filter(Boolean)[0];
  const crumb = seg ? (seg in CRUMB_KEYS ? t(CRUMB_KEYS[seg]) : toTitle(seg)) : null;

  return (
    <header className="sticky top-0 z-30 flex h-11 items-center gap-3 border-b border-border/60 bg-background/85 px-4 backdrop-blur-md">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Link
          to="/chat"
          className="flex items-center gap-2 text-[13px] font-semibold tracking-tight text-foreground hover:text-primary"
        >
          {t("brand.name")}
        </Link>
        <Badge
          variant="lemon"
          className="hidden h-4 px-1.5 text-[9px] uppercase tracking-[0.1em] sm:inline-flex"
        >
          {t("brand.beta")}
        </Badge>
        {crumb ? (
          <>
            <span className="text-muted-foreground/60">/</span>
            <span className="truncate text-[12px] text-muted-foreground">{crumb}</span>
          </>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5">
        <LangToggle />
        <ThemeToggle />
        {children}
      </div>
    </header>
  );
}

function toTitle(seg: string): string {
  return seg
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}
